import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

// Customer portal display date format
export const DATE_FORMAT = 'DD-MM-YYYY';
// API DTO date format
export const API_DATE_FORMAT = 'YYYY-MM-DD';
// Long, human-readable display date format (e.g. "27 April 2026")
export const LONG_DATE_FORMAT = 'D MMMM YYYY';
const TIME_FORMAT = 'HH:mm:ss';

/**
 * Format time (Hours, Mins, Seconds) from a given DateTime ISO string
 * @param dateStr The serialized DateTime ISO string
 * @param timezone The output timezone, leaving undefined results in localized time
 * @returns Formatted time
 */
export const formatHours = (dateStr: string, timezone: string = 'utc') => {
  const formattedHours =
    timezone === 'utc'
      ? dayjs.utc(dateStr).format(TIME_FORMAT)
      : dayjs(dateStr).format(TIME_FORMAT);

  if (formattedHours === 'Invalid Date')
    throw Error(`Error formatting date string: ${dateStr}.`);

  return formattedHours;
};

/**
 * Format date from a given DateTime ISO string
 * @param dateStr The serialized DateTime ISO string
 * @param timezone The output timezone, leaving undefined results in localized time
 * @param format The output format, leaving undefined results in "standard" format
 * @returns Formatted time
 */
export const formatDate = (
  dateStr: string,
  timezone?: 'utc',
  format?: 'iso'
) => {
  // Trying to format a 'DD-MM-YYYY' dateStr to DATE_FORMAT (also 'DD-MM-YYYY') will fail with 'Invalid Date'
  // Instead of failing, lets just return the formatted dateStr
  if ((!timezone || !format) && dayjs(dateStr, DATE_FORMAT).isValid()) {
    return dateStr;
  }

  const formattedDate =
    timezone === 'utc'
      ? format === 'iso'
        ? dayjs.utc(dateStr, DATE_FORMAT).format(API_DATE_FORMAT)
        : dayjs.utc(dateStr).format(DATE_FORMAT)
      : dayjs(dateStr).format(DATE_FORMAT);

  if (formattedDate === 'Invalid Date')
    throw Error(`Error formatting date string: ${dateStr}.`);

  return formattedDate;
};

/**
 * Format a date as a long, human-readable string (e.g. "27 April 2026").
 * Accepts both an ISO string and a `Date` — broader than the sibling
 * helpers, which only take a `string`.
 * @param date The date to format (ISO string or `Date`)
 * @returns Formatted long date
 */
export const formatLongDate = (date: string | Date) => {
  const formatted = dayjs(date).format(LONG_DATE_FORMAT);

  if (formatted === 'Invalid Date')
    throw Error(`Error formatting date: ${String(date)}.`);

  return formatted;
};

export { Dayjs } from 'dayjs';
export default dayjs;
